import { useState } from "react";
import {
  PanelShell, Badge, Button, ApiResourceView, TextField, PillToggleGroup, CheckboxRow, tokens,
  DJSEMApiClient, useApiResource, useApiAction, analyseLibraryHealth,
  type LibraryTrackRow,
} from "../index";
import type { DJSEMPlatform } from "../platform";

/**
 * Library module — the top-level library screen: an at-a-glance health summary for the user's
 * track library, followed by the Rekordbox import/export panel.
 *
 * @param client - The API client used to fetch tracks and compute library health.
 * @param platform - The current platform (web/desktop); determines whether Rekordbox
 *   import/export is available inline or shown as a desktop-only notice.
 * @returns A library health panel plus the Rekordbox import/export module.
 */
export function LibraryModule({ client, platform }: { client: DJSEMApiClient; platform: DJSEMPlatform }) {
  const res = useApiResource(() => client.listTracks(), [], (d) => (d.items?.length ?? 0) === 0);
  return (
    <>
      <ApiResourceView
        resource={res}
        empty={{ title: "Your library is empty", detail: "Import local music, a Rekordbox XML, or a Spotify/SoundCloud playlist (Ctrl+K → import)." }}
        render={(data) => {
          const rows = (data.items ?? []) as unknown as LibraryTrackRow[];
          const health = analyseLibraryHealth(rows);
          return (
            <PanelShell title="Library health">
              <div>{rows.length} tracks</div>
              <div>Health score: {(health.healthScore * 100).toFixed(0)}%</div>
              <div>{health.issues.length} issue{health.issues.length === 1 ? "" : "s"}</div>
            </PanelShell>
          );
        }}
      />
      <RekordboxModule client={client} platform={platform} />
    </>
  );
}

/**
 * Rekordbox import/export (prompts 04A/04C/04D). Desktop-only regardless of plan
 * (ARTIST_IDE_GUARDRAIL_V3.md §4 — "Desktop app = everything in web plus... Rekordbox
 * import/export"). A thin, hookless wrapper so the platform branch can early-return before
 * RekordboxPanels' hooks run — same shape as StemStudioModule/VideoGenerationModule.
 */
function RekordboxModule({ client, platform }: { client: DJSEMApiClient; platform: DJSEMPlatform }) {
  if (platform.kind === "web") {
    return (
      <PanelShell title="Rekordbox import / export" style={{ marginTop: 12 }}>
        <div style={{ display: "flex", flexDirection: "column", gap: 10, maxWidth: 560 }}>
          <div style={{ fontSize: tokens.text.lg, color: tokens.color.text }}>
            Rekordbox import/export runs in the DJSEM desktop app.
          </div>
          <p style={{ margin: 0, color: tokens.color.textDim, fontSize: tokens.text.md, lineHeight: 1.55 }}>
            The browser app covers your library, sets, visuals, brand and release assets.
            Rekordbox is itself a desktop-only DJ tool, so moving a library in or out happens on
            the same machine — the desktop Studio has that workflow.
          </p>
          <div style={{ marginTop: 4 }}>
            <Badge tone="neutral">Desktop feature</Badge>
          </div>
        </div>
      </PanelShell>
    );
  }
  return <RekordboxPanels client={client} />;
}

type RekordboxExportMode = "playlist_only" | "playlist_with_track_refs" | "playlist_with_cues";
const EXPORT_MODES: readonly RekordboxExportMode[] = ["playlist_only", "playlist_with_track_refs", "playlist_with_cues"];
const EXPORT_MODE_LABELS: Record<RekordboxExportMode, string> = {
  playlist_only: "Playlist only", playlist_with_track_refs: "With metadata", playlist_with_cues: "With cues",
};
const EXPORT_MODE_HINTS: Record<RekordboxExportMode, string> = {
  playlist_only: "Just track order in a playlist — no metadata.",
  playlist_with_track_refs: "BPM, key, duration and file location included.",
  playlist_with_cues: "Metadata plus approved cue points.",
};

function RekordboxPanels({ client }: { client: DJSEMApiClient }) {
  // ---- import ----
  const [importXml, setImportXml] = useState("");
  const [importFileName, setImportFileName] = useState<string | null>(null);
  const importAction = useApiAction((xml: string) => client.importRekordbox(xml));

  const onChooseFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImportFileName(file.name);
    const reader = new FileReader();
    reader.onload = () => setImportXml(String(reader.result ?? ""));
    reader.readAsText(file);
  };

  // ---- export ----
  const [playlistName, setPlaylistName] = useState("My DJSEM Export");
  const [exportMode, setExportMode] = useState<RekordboxExportMode>("playlist_with_track_refs");
  const [includeCues, setIncludeCues] = useState(false);
  const [confirmedNoOverwrite, setConfirmedNoOverwrite] = useState(false);
  const [confirmedCueExport, setConfirmedCueExport] = useState(false);
  const [lastDownload, setLastDownload] = useState<{ filename: string; trackCount: number } | null>(null);
  const exportAction = useApiAction(async () => {
    const tracksRes = await client.listTracks();
    if (!tracksRes.ok) return tracksRes;
    const rows = (tracksRes.data.items ?? []) as unknown as LibraryTrackRow[];
    if (rows.length === 0) return { ok: false as const, error: { code: "empty_library", message: "No tracks in your library to export." } };

    const tracksById: Record<string, unknown> = {};
    rows.forEach((t) => { tracksById[t.id] = { preparationTrackId: t.id, title: t.title, artist: t.artist, bpm: t.bpm, key: t.key, durationMs: t.durationSec ? t.durationSec * 1000 : undefined }; });
    const set = {
      id: "set_library_export", title: playlistName || "My DJSEM Export",
      tracks: rows.map((t, i) => ({ id: `st_${t.id}`, preparationTrackId: t.id, position: i, locked: false, warnings: [] })),
      chapters: [], setArc: "custom", createdAt: new Date(0).toISOString(), updatedAt: new Date(0).toISOString(),
      reviewStatus: "draft", liveReadiness: { approvedForLive: false, blockers: [], warnings: [] },
    };
    const filename = `${(playlistName || "djsem-export").replace(/[^A-Za-z0-9 ._-]/g, "_").slice(0, 100)}.xml`;
    const result = await client.exportRekordbox({
      setIds: [set.id], outputFilename: filename, exportMode,
      includeTrackMetadata: exportMode !== "playlist_only", includeCues, includeOnlyApprovedCues: true,
      sets: [set], tracksById, userConfirmedNoOverwrite: confirmedNoOverwrite,
      userConfirmedCueExport: includeCues ? confirmedCueExport : undefined,
    });
    if (result.ok) {
      const blob = new Blob([result.data.xml], { type: "application/xml" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url; a.download = filename; a.click();
      URL.revokeObjectURL(url);
      setLastDownload({ filename, trackCount: result.data.manifest.trackCount });
    }
    return result;
  });

  return (
    <>
      <PanelShell title="Rekordbox import" style={{ marginTop: 12 }}>
        <div style={{ fontSize: 12, color: tokens.color.textDim, marginBottom: 10 }}>
          Read-only: parses a Rekordbox XML export into DJSEM. Never writes back to your Rekordbox library.
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
          <input type="file" accept=".xml" onChange={onChooseFile} style={{ fontSize: 12 }} />
          {importFileName ? <span style={{ fontSize: 12, color: tokens.color.textDim }}>{importFileName}</span> : null}
          <Button disabled={!importXml || importAction.pending} onClick={() => void importAction.run(importXml)}>
            {importAction.pending ? "Importing…" : "Import"}
          </Button>
        </div>
        {importAction.view?.state === "ready" && importAction.view.data ? (
          <div style={{ marginTop: 10 }}>
            <Badge tone="ok">{importAction.view.data.report.tracksCreated} tracks, {importAction.view.data.sets.length} playlist{importAction.view.data.sets.length === 1 ? "" : "s"} parsed</Badge>
            {importAction.view.data.report.warnings.map((w, i) => <div key={i} style={{ fontSize: 11, color: "#e8b24a", marginTop: 4 }}>{w}</div>)}
          </div>
        ) : null}
        {importAction.view?.state === "error" ? <div style={{ marginTop: 10, fontSize: 12, color: tokens.color.danger }}>{importAction.view.message}</div> : null}
      </PanelShell>

      <PanelShell title="Rekordbox export" style={{ marginTop: 12 }}>
        <div style={{ fontSize: 12, color: tokens.color.textDim, marginBottom: 10 }}>
          Exports your library as a NEW Rekordbox-compatible XML file. Never overwrites an existing library, database, or USB.
        </div>
        <TextField value={playlistName} onChange={setPlaylistName} placeholder="Playlist name"
          style={{ width: "100%", boxSizing: "border-box", padding: "8px 10px", fontSize: 12, marginBottom: 10 }} />
        <PillToggleGroup options={EXPORT_MODES} value={exportMode} onChange={setExportMode}
          labelFor={(o) => EXPORT_MODE_LABELS[o]} titleFor={(o) => EXPORT_MODE_HINTS[o]} />
        <div style={{ marginTop: 10 }}>
          <CheckboxRow checked={includeCues} onChange={setIncludeCues}>Include approved cue points (experimental)</CheckboxRow>
          {includeCues ? (
            <CheckboxRow checked={confirmedCueExport} onChange={setConfirmedCueExport}>I understand cue export is experimental</CheckboxRow>
          ) : null}
          <CheckboxRow checked={confirmedNoOverwrite} onChange={setConfirmedNoOverwrite}>
            I understand this creates a new file and never overwrites my Rekordbox library
          </CheckboxRow>
        </div>
        <div style={{ marginTop: 12 }}>
          <Button
            disabled={!confirmedNoOverwrite || (includeCues && !confirmedCueExport) || exportAction.pending}
            onClick={() => void exportAction.run()}
          >{exportAction.pending ? "Exporting…" : "Export & download"}</Button>
        </div>
        {lastDownload ? (
          <div style={{ marginTop: 10 }}>
            <Badge tone="ok">Downloaded {lastDownload.filename} · {lastDownload.trackCount} tracks</Badge>
          </div>
        ) : null}
        {exportAction.view?.state === "error" ? <div style={{ marginTop: 10, fontSize: 12, color: tokens.color.danger }}>{exportAction.view.message}</div> : null}
      </PanelShell>
    </>
  );
}
